import type { MonospacePage } from '#layers/data-model/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { useCurrentSpace } from '#layers/spaces/composables/use-current-space';

type PageIntent = NonNullable<MonospacePage['intent']>;

interface GetPageWithIntentOptions {
	includeGlobalFallback?: boolean;
}

export function usePageIntent() {
	const store = useWorkspaceEntityStore();
	const { currentSpace } = useCurrentSpace();

	/**
	 * Resolve the page that should be used for a collection given a desired intent.
	 *
	 * Lookup happens per scope (in-space first, then global by default). Within each scope the intents in
	 * `intentsToTry` are attempted in order, then a final no-intent (`null`) lookup. A `'create'`
	 * intent falls back to `'edit'` since create and edit now share a single customizable form.
	 */
	function getPageWithIntent(
		collectionId: string,
		type: NonNullable<MonospacePage['type']>,
		intent?: MonospacePage['intent'],
		spaceId?: string,
		options: GetPageWithIntentOptions = {},
	) {
		const { includeGlobalFallback = true } = options;

		// 'create' reuses the 'edit' form when no dedicated create page exists.
		const intentsToTry: Array<PageIntent | null> = [];

		if (intent === 'create') {
			intentsToTry.push('create', 'edit');
		}
		else if (intent) {
			intentsToTry.push(intent);
		}

		intentsToTry.push(null);

		function lookupInSpace(finalSpaceId: string, lookupIntent: MonospacePage['intent']) {
			return store.MonospacePage.peekFirst({
				filter: {
					type: { _eq: type },
					intent: { _eq: lookupIntent },
					collectionId: { _eq: collectionId },
					spaceId: { _eq: finalSpaceId },
				},
			});
		}

		function lookupGlobally(lookupIntent: MonospacePage['intent']) {
			return store.MonospacePage.peekFirst({
				filter: {
					type: { _eq: type },
					intent: { _eq: lookupIntent },
					collectionId: { _eq: collectionId },
				},
			});
		}

		function resolveInSpace(finalSpaceId?: string) {
			if (!finalSpaceId) {
				return;
			}

			for (const lookupIntent of intentsToTry) {
				const page = lookupInSpace(finalSpaceId, lookupIntent);

				if (page) {
					return page;
				}
			}
		}

		function resolveGlobally() {
			for (const lookupIntent of intentsToTry) {
				const page = lookupGlobally(lookupIntent);

				if (page) {
					return page;
				}
			}
		}

		if (spaceId) {
			return resolveInSpace(spaceId) ?? (includeGlobalFallback ? resolveGlobally() : undefined);
		}

		return resolveInSpace(currentSpace.value?.id) ?? (includeGlobalFallback ? resolveGlobally() : undefined);
	}

	return {
		getPageWithIntent,
	};
}
